import { fetchApi } from '@inmagik/react-crud'
import { PartialWithId } from '@inmagik/react-crud/dist/types'
import dayjs from 'dayjs'
import { FormikHelpers } from 'formik'
import useModalTrigger from 'magik-react-hooks/useModalTrigger'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Card, CardBody, Spinner } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import { useAuth } from '../../auth'
import MaterialIcon from '../../components/MaterialIcon'
import ModalConfirm from '../../components/ModalConfirm'
import ModalForm from '../../components/ModalForm'
import { API_URL } from '../../constants'
import { useBilancia, usePartialUpdateBilancia } from '../../hooks/bilance'
import { useCreateIntervento, usePartialUpdateIntervento } from '../../hooks/interventi'
import { Bilancia, Intervento } from '../../types'
import { transformDownloadUrl, transformErrorForForm, useIsAmministratore, useIsSupervisore } from '../../utils/utils'
import FormBilancia from '../Bilance/FormBilancia'
import CardAllegatiBilancia from './CardAllegatiBilancia'
import CardCollaudo from './CardCollaudo'
import CardDatiBilancia from './CardDatiBilancia'
import CardRapportoTaraturaBilancia from './CardRapportoTaraturaBilancia'
import CardStoricoInterventiBilancia from './CardStoricoInterventiBilancia'
import CardVerificazionePeriodicaBilancia from './CardVerificazionePeriodicaBilanica'
import FormInterventoVeloce from './FormInterventoVeloce'
import FormProgrammaIntervento from './FormProgrammaIntervento'
import FormRichiesta from './FormRichiesta'

const initialValues = {
  matricola: '',
  tipo_bilancia: '',
  tipo_bilancia_em: '',
  nome_produttore: '',
  sede: undefined,
}

const initialValuesIntervento = {
  data_intervento: '',
  data_richiesta: '',
  tipo_intervento: '',
  esito: '',
  note: '',
  utente: undefined,
  cert_approv: '',
}

const schema = yup.object().shape({
  matricola: yup.string().required().label('Matricola'),
  tipo_bilancia: yup.string().required().label('Tipo bilancia'),
  nome_produttore: yup.string().required().label('Nome produttore'),
  sede: yup.number().required().label('Sede'),
  tipo_bilancia_em: yup.string().required().label('Tipo bilancia'),
  cert_approv: yup
    .string()
    .label('Provvedimento')
    .when('verif_periodica', (verif_periodica: any, schema: any) => {
      if (verif_periodica[0] !== 'false' && verif_periodica[0] !== false) {
        return schema.required()
      } else {
        return schema.notRequired()
      }
    }),
  anno_marcatura_ce: yup
    .string()
    .nullable()
    .matches(/^[0-9]{4}$/, 'Deve essere un anno a 4 cifre')
    .label('Anno marcatura CE'),
})

const schemaIntervento = yup.object().shape({
  data_richiesta: yup.string().required().label('Data richiesta'),
})

const schemaInterventoVeloceTaratura = yup.object().shape({
  data_intervento: yup.string().required().label('Data intervento'),
})

const schemaInterventoVelocePeriodica = yup.object().shape({
  data_richiesta: yup.string().required().label('Data richiesta'),
  data_intervento: yup.string().required().label('Data intervento'),
})

export default function BilanciaDettaglio() {
  const { id } = useParams()
  const bilancia = useBilancia(String(id))

  const [modalStateForm, modalActionsForm] = useModalTrigger<Bilancia>()

  const [modalStateAddRichiesta, modalActionsAddRichiesta] = useModalTrigger<PartialWithId<Intervento>>()

  const [modalStateAddInterventoVeloce, modalActionsAddInterventoVeloce] = useModalTrigger<PartialWithId<Intervento>>()

  const [modalStateProgrammaIntervento, modalActionsProgrammaIntervento] = useModalTrigger<PartialWithId<Intervento>>()

  const { mutateAsync: updatePartialBilancia } = usePartialUpdateBilancia()
  const { mutateAsync: updatePartialInterventoJson } = usePartialUpdateIntervento('json')

  const { mutateAsync: createIntervento } = useCreateIntervento()

  const inputDocAnagraficaPesa = useRef<HTMLInputElement>(null)
  const inputDocComunicazInstallaz = useRef<HTMLInputElement>(null)
  const inputDocModelloE = useRef<HTMLInputElement>(null)
  const inputDocDichiarazUeOm = useRef<HTMLInputElement>(null)

  const { tokens } = useAuth()

  const saveBilancia = useCallback(
    (values: PartialWithId<Bilancia>, actions: FormikHelpers<Bilancia>) => {
      // remove documenti from bilancia
      const bilancia: PartialWithId<Bilancia> = Object.assign({}, values)
      delete bilancia.doc_anagrafica_pesa
      delete bilancia.doc_comunicaz_installaz
      delete bilancia.doc_modello_e
      delete bilancia.doc_dichiaraz_ue_om
      // remove storico!
      delete bilancia.storico_interventi
      if (typeof bilancia.collaudo_xlsm === 'string') {
        delete bilancia.collaudo_xlsm
      }
      delete bilancia.foto_targa
      return updatePartialBilancia({
        ...bilancia,
        sede: bilancia?.sede,
        matricole_celle: bilancia.matricole_celle_content?.join(' '),
      })
        .then(() => {
          modalActionsForm.close()
          toast.success('Bilancia aggiornata con successo', {
            position: 'bottom-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          })
          actions.resetForm()
        })
        .catch((e) => {
          actions.setErrors(transformErrorForForm(e))
        })
    },
    [modalActionsForm, modalStateForm, updatePartialBilancia]
  )

  const saveRichiestaIntervento = useCallback(
    (intervento: PartialWithId<Intervento>, actions: FormikHelpers<Intervento>) => {
      return createIntervento({
        ...intervento,
        bilancia: bilancia.data?.id,
      })
        .then(() => {
          modalActionsAddRichiesta.close()
          toast.success('Richiesta intervento periodica creata con successo', {
            position: 'bottom-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          })
          actions.resetForm()
          bilancia.refetch()
        })
        .catch((e) => {
          actions.setErrors(transformErrorForForm(e))
        })
    },
    [bilancia.data?.id, createIntervento, modalActionsAddRichiesta]
  )

  const saveInterventoVeloce = useCallback(
    (intervento: PartialWithId<Intervento>, actions: FormikHelpers<Intervento>) => {
      return createIntervento({
        ...intervento,
        bilancia: bilancia.data?.id,
        stato: 'programmato',
        durata_stimata:
          // add 00 seconds to the time
          intervento.durata_stimata?.length === 5 ? `${intervento.durata_stimata}:00` : intervento.durata_stimata,
      })
        .then((data) => {
          modalActionsAddInterventoVeloce.close()
          toast.success('Intervento veloce creato con successo', {
            position: 'bottom-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          })
          actions.resetForm()
          bilancia.refetch()
          if (intervento.tipo_intervento === 'rapporto_taratura') {
            navigate(`/interventi/${data.id}`)
          }
        })
        .catch((e) => {
          actions.setErrors(transformErrorForForm(e))
        })
    },
    [bilancia.data?.id, createIntervento, modalActionsAddInterventoVeloce]
  )

  const saveProgrammaIntervento = useCallback(
    (intervento: PartialWithId<Intervento>, actions: FormikHelpers<Intervento>) => {
      if (intervento.pk) {
        return updatePartialInterventoJson({
          data_intervento: intervento.data_intervento,
          utente: intervento.utente,
          bilancia: bilancia.data?.id,
          id: intervento.pk,
          stato: 'programmato',
          durata_stimata:
            // add 00 seconds to the time
            intervento.durata_stimata?.length === 5 ? `${intervento.durata_stimata}:00` : intervento.durata_stimata,
        })
          .then(() => {
            modalActionsProgrammaIntervento.close()
            toast.success('Intervento programmato creato con successo', {
              position: 'bottom-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            })
            actions.resetForm()
            bilancia.refetch()
          })
          .catch((e) => {
            actions.setErrors(transformErrorForForm(e))
          })
      } else {
        return createIntervento({
          ...intervento,
          bilancia: bilancia.data?.id,
          stato: 'programmato',
          utente: intervento.utente,
        })
          .then(() => {
            modalActionsProgrammaIntervento.close()
            toast.success('Intervento programmato creato con successo', {
              position: 'bottom-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            })
            actions.resetForm()
            bilancia.refetch()
          })
          .catch((e) => {
            actions.setErrors(transformErrorForForm(e))
          })
      }
    },
    [bilancia.data?.id, createIntervento, modalActionsProgrammaIntervento]
  )

  const [isOpenEditDelete, setIsOpenEditDelete] = useState(false)

  const editDeleteRef = useRef<HTMLDivElement>(null)

  const [modalStateDelete, modalActionsDelete] = useModalTrigger<Bilancia>()

  const navigate = useNavigate()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editDeleteRef.current && !editDeleteRef.current.contains(event.target as Node)) {
        setIsOpenEditDelete(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [editDeleteRef])

  const isAdmin = useIsAmministratore()
  const isSupervisore = useIsSupervisore()

  useEffect(() => {
    // scroll to top
    window.scrollTo(0, 0)
  }, [])

  if (bilancia.isLoading) {
    return (
      <div className="page">
        <div className="h-100 d-flex justify-content-center align-items-center">
          <Spinner animation="border" role="status" variant="secondary" />
        </div>
      </div>
    )
  }

  return (
    <div className="page">
      <div className="row mt-4">
        <div className="col-12">
          <div className="d-flex align-items-center">
            <div
              onClick={() => {
                navigate(-1)
              }}
              className="p-sm no-link d-flex align-items-center pointer"
            >
              <span className="material-icons text-secondary">scale</span>
              <span className="ms-2">Lista bilance</span>
            </div>
            <span className="material-icons text-secondary">chevron_right</span>
            <span className="ms-2 p-sm bg-secondary-light p-2 text-secondary rounded">Dettaglio bilancia</span>
          </div>
        </div>
        <div className="col-12 d-flex align-items-center">
          <div>
            <h1 className="title me-4 mb-0">
              Bilancia {bilancia.data?.matricola} {bilancia.data?.sigla ? `(${bilancia.data?.sigla})` : ''}
            </h1>
          </div>

          <div className="d-flex align-items-center position-relative">
            <Button
              onClick={() => {
                setIsOpenEditDelete(!isOpenEditDelete)
              }}
              variant="light"
              className="text-secondary d-flex align-items-center"
              type="button"
            >
              <span className="material-icons">more_horiz</span>
            </Button>
            {isOpenEditDelete && (
              <div
                className="list-group position-absolute"
                ref={editDeleteRef}
                style={{
                  zIndex: 1000,
                  width: '200px',
                  boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',
                  top: '40px',
                  borderRadius: '5px',
                }}
              >
                <button
                  onClick={() => {
                    navigate(`/bilance/crea?duplicate_from=${bilancia.data?.id}`)
                  }}
                  className="list-group-item list-group-item-action d-flex align-items-center"
                >
                  <MaterialIcon icon="edit" className="me-2" />
                  Duplica
                </button>
                {(isAdmin || isSupervisore) && (
                  <button
                    onClick={() => {
                      modalActionsDelete.open(bilancia.data)
                    }}
                    className="list-group-item list-group-item-action d-flex align-items-center text-danger"
                  >
                    <MaterialIcon icon="delete" className="me-2" />
                    Elimina
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="row mt-2">
        <div className="col-md-6">
          <CardDatiBilancia bilancia={bilancia} modalActionsForm={modalActionsForm} />
          <Card className="mt-3 mb-3">
            <CardBody>
              <div className="py-1 border-bottom">
                <div className="d-flex justify-content-between">
                  <h4 className="heading-h4">Cliente</h4>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-md-8">
                  <div className="label-m text-body-secondary">Nome cliente</div>
                  <div className="p-md mt-2">
                    <Link to={`/clienti/${bilancia.data?.cliente_pk}`} className="link-md text-body-secondary">
                      {bilancia.data?.cliente_ragione_sociale}
                    </Link>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="label-m text-body-secondary">Numero REA</div>
                  <div className="p-md mt-2">{bilancia.data?.sede_data?.numero_rea}</div>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-md-8">
                  <div className="label-m text-body-secondary">Indirizzo</div>
                  <div className="p-md mt-2">
                    {bilancia.data?.sede_data?.toponimo_data?.nome} {bilancia.data?.sede_data?.via}
                    {bilancia.data?.sede_data?.civico && `, ${bilancia.data?.sede_data?.civico}`}-{' '}
                    {bilancia.data?.sede_data?.comune_data?.nome} ({bilancia.data?.sede_data?.provincia_data?.sigla})
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="label-m text-body-secondary">Reparto</div>
                  <div className="p-md mt-2">{bilancia.data?.reparto}</div>
                </div>
                <div className="col-md-4 mt-3">
                  <div className="label-m text-body-secondary">Zona utilizzo</div>
                  <div className="p-md mt-2">{bilancia.data?.zona_utilizzo}</div>
                </div>
              </div>
            </CardBody>
          </Card>
          {bilancia.data?.autoprodotta && (isAdmin || isSupervisore) && (
            <Card className="mt-3 mb-3">
              <CardBody>
                <div className="py-1 border-bottom">
                  <div className="d-flex justify-content-between">
                    <h4 className="heading-h4">Documenti</h4>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-md-6 d-flex flex-column">
                    <div className="d-flex align-items-center">
                      <span className="material-icons text-secondary me-2">description</span>
                      <div>
                        <span>Dati anagrafici della pesa</span>
                        <div className="p-sm">
                          {bilancia.data?.doc_anagrafica_pesa_data_generazione
                            ? dayjs(bilancia.data?.doc_anagrafica_pesa_data_generazione).format('DD.MM.YYYY')
                            : '-'}
                        </div>
                        {dayjs(bilancia.data?.doc_anagrafica_pesa_data_generazione).diff(
                          dayjs(bilancia.data?.data_ultima_modifica),
                          'days'
                        ) < 0 && (
                          <div className="p-sm text-danger mt-2">
                            File obsoleto, (data modifica:{' '}
                            {dayjs(bilancia.data?.data_ultima_modifica).format('DD.MM.YYYY')})
                          </div>
                        )}
                      </div>
                    </div>

                    {/* if last_update_date is bigger than doc_anagrafica_pesa_data_generazione, show a warning */}
                  </div>
                  <div className="col-md-2">
                    {(isAdmin || isSupervisore) && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="w-100"
                        onClick={async () => {
                          await fetchApi(
                            `${API_URL}/bilance/bilancia/${bilancia.data?.id}/genera-doc-anagrafica-bilancia/`,
                            {
                              method: 'POST',
                              headers: {
                                Authorization: `Bearer ${tokens?.access}`,
                              },
                            }
                          ).then(() => {
                            bilancia.refetch()
                            toast.success('Documento Anagrafica Pesa generato con successo', {
                              position: 'bottom-right',
                              autoClose: 5000,
                              hideProgressBar: false,
                              closeOnClick: true,
                              pauseOnHover: true,
                              draggable: true,
                              progress: undefined,
                            })
                          })
                        }}
                      >
                        {bilancia.data?.doc_anagrafica_pesa_data_generazione ? 'Rigenera' : 'Genera'}
                      </Button>
                    )}
                  </div>
                  <div className="col-md-2">
                    {(isAdmin || isSupervisore) && (
                      <Button
                        size="sm"
                        variant="secondary"
                        className="w-100 text-white"
                        onClick={() => {
                          inputDocAnagraficaPesa.current?.click()
                        }}
                      >
                        Carica
                      </Button>
                    )}
                    <input
                      type="file"
                      ref={inputDocAnagraficaPesa}
                      hidden
                      onChange={async (e) => {
                        await updatePartialBilancia({
                          id: bilancia.data?.id,
                          doc_anagrafica_pesa: e.target.files?.[0],
                          doc_anagrafica_pesa_data_generazione: dayjs().format('YYYY-MM-DD'),
                        })
                      }}
                    />
                  </div>
                  <div className="col-md-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        const url = new URL(
                          bilancia.data?.doc_anagrafica_pesa
                            ? transformDownloadUrl(String(bilancia.data?.doc_anagrafica_pesa))
                            : '#'
                        )
                        window.open(url.toString(), '_blank')
                      }}
                      disabled={!bilancia.data?.doc_anagrafica_pesa}
                      className="w-100 text-white"
                    >
                      Scarica
                    </Button>
                  </div>
                  {bilancia.data?.doc_anagrafica_pesa && !bilancia.data.doc_anagrafica_pesa_generato && (
                    <div className="offset-md-6 col-md-6">
                      <div className="p-sm text-body-secondary">Caricato manualmente</div>
                    </div>
                  )}
                </div>
                <div className="row mt-3">
                  <div className="col-md-6 d-flex flex-column">
                    <div className="d-flex align-items-center">
                      <span className="material-icons text-secondary me-2">description</span>
                      <div>
                        <span>Comunicazione installazione</span>
                        <div className="p-sm">
                          {bilancia.data?.doc_comunicaz_installaz_data_generazione
                            ? dayjs(bilancia.data?.doc_comunicaz_installaz_data_generazione).format('DD.MM.YYYY')
                            : '-'}
                        </div>
                        {dayjs(bilancia.data?.doc_comunicaz_installaz_data_generazione).diff(
                          dayjs(bilancia.data?.data_ultima_modifica),
                          'days'
                        ) < 0 && (
                          <div className="p-sm text-danger mt-2">
                            File obsoleto, (data modifica:{' '}
                            {dayjs(bilancia.data?.data_ultima_modifica).format('DD.MM.YYYY')})
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    {(isAdmin || isSupervisore) && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="w-100"
                        onClick={async () => {
                          await fetchApi(
                            `${API_URL}/bilance/bilancia/${bilancia.data?.id}/genera-doc-comunicazione-installazione/`,
                            {
                              method: 'POST',
                              headers: {
                                Authorization: `Bearer ${tokens?.access}`,
                              },
                            }
                          ).then(() => {
                            bilancia.refetch()
                            toast.success('Documento Comunicazione Installazione generato con successo', {
                              position: 'bottom-right',
                              autoClose: 5000,
                              hideProgressBar: false,
                              closeOnClick: true,
                              pauseOnHover: true,
                              draggable: true,
                              progress: undefined,
                            })
                          })
                        }}
                      >
                        {bilancia.data?.doc_comunicaz_installaz_data_generazione ? 'Rigenera' : 'Genera'}
                      </Button>
                    )}
                  </div>
                  <div className="col-md-2">
                    {(isAdmin || isSupervisore) && (
                      <Button
                        size="sm"
                        variant="secondary"
                        className="w-100 text-white"
                        onClick={() => {
                          inputDocComunicazInstallaz.current?.click()
                        }}
                      >
                        Carica
                      </Button>
                    )}
                    <input
                      type="file"
                      ref={inputDocComunicazInstallaz}
                      hidden
                      onChange={async (e) => {
                        await updatePartialBilancia({
                          id: bilancia.data?.id,
                          doc_comunicaz_installaz: e.target.files?.[0],
                          doc_comunicaz_installaz_data_generazione: dayjs().format('YYYY-MM-DD'),
                        })
                      }}
                    />
                  </div>
                  <div className="col-md-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="w-100 text-white"
                      onClick={() => {
                        const url = new URL(
                          bilancia.data?.doc_comunicaz_installaz
                            ? transformDownloadUrl(String(bilancia.data?.doc_comunicaz_installaz))
                            : '#'
                        )
                        window.open(url.toString(), '_blank')
                      }}
                      disabled={!bilancia.data?.doc_comunicaz_installaz}
                    >
                      Scarica
                    </Button>
                  </div>
                  {bilancia.data?.doc_comunicaz_installaz && !bilancia.data.doc_comunicaz_installaz_generato && (
                    <div className="offset-md-6 col-md-6">
                      <div className="p-sm text-body-secondary">Caricato manualmente</div>
                    </div>
                  )}
                </div>
                <div className="row mt-3">
                  <div className="col-md-6 d-flex flex-column">
                    <div className="d-flex align-items-center">
                      <span className="material-icons text-secondary me-2">description</span>
                      <div>
                        <span>Modello E</span>
                        <div className="p-sm">
                          {bilancia.data?.doc_modello_e_data_generazione
                            ? dayjs(bilancia.data?.doc_modello_e_data_generazione).format('DD.MM.YYYY')
                            : '-'}
                        </div>
                        {dayjs(bilancia.data?.doc_modello_e_data_generazione).diff(
                          dayjs(bilancia.data?.data_ultima_modifica),
                          'days'
                        ) < 0 && (
                          <div className="p-sm text-danger mt-2">
                            File obsoleto, (data modifica:{' '}
                            {dayjs(bilancia.data?.data_ultima_modifica).format('DD.MM.YYYY')})
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    {(isAdmin || isSupervisore) && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="w-100"
                        onClick={async () => {
                          await fetchApi(`${API_URL}/bilance/bilancia/${bilancia.data?.id}/genera-doc-modello-e/`, {
                            method: 'POST',
                            headers: {
                              Authorization: `Bearer ${tokens?.access}`,
                            },
                          }).then(() => {
                            bilancia.refetch()
                            toast.success('Documento Modello E generato con successo', {
                              position: 'bottom-right',
                              autoClose: 5000,
                              hideProgressBar: false,
                              closeOnClick: true,
                              pauseOnHover: true,
                              draggable: true,
                              progress: undefined,
                            })
                          })
                        }}
                      >
                        {bilancia.data?.doc_modello_e_data_generazione ? 'Rigenera' : 'Genera'}
                      </Button>
                    )}
                  </div>
                  <div className="col-md-2">
                    {(isAdmin || isSupervisore) && (
                      <Button
                        size="sm"
                        variant="secondary"
                        className="w-100 text-white"
                        onClick={() => {
                          inputDocModelloE.current?.click()
                        }}
                      >
                        Carica
                      </Button>
                    )}
                    <input
                      type="file"
                      ref={inputDocModelloE}
                      hidden
                      onChange={async (e) => {
                        await updatePartialBilancia({
                          id: bilancia.data?.id,
                          doc_modello_e: e.target.files?.[0],
                          doc_modello_e_data_generazione: dayjs().format('YYYY-MM-DD'),
                        })
                      }}
                    />
                  </div>
                  <div className="col-md-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="w-100 text-white"
                      onClick={() => {
                        const url = new URL(
                          bilancia.data?.doc_modello_e
                            ? transformDownloadUrl(String(bilancia.data?.doc_modello_e))
                            : '#'
                        )
                        window.open(url.toString(), '_blank')
                      }}
                      disabled={!bilancia.data?.doc_modello_e}
                    >
                      Scarica
                    </Button>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-md-6 d-flex flex-column">
                    <div className="d-flex align-items-center">
                      <span className="material-icons text-secondary me-2">description</span>
                      <div>
                        <span>Dichiarazione UE-om</span>
                        <div className="p-sm">
                          {bilancia.data?.doc_dichiaraz_ue_om_data_generazione
                            ? dayjs(bilancia.data?.doc_dichiaraz_ue_om_data_generazione).format('DD.MM.YYYY')
                            : '-'}
                        </div>
                        {dayjs(bilancia.data?.doc_dichiaraz_ue_om_data_generazione).diff(
                          dayjs(bilancia.data?.data_ultima_modifica),
                          'days'
                        ) < 0 && (
                          <div className="p-sm text-danger mt-2">
                            File obsoleto, (data modifica:{' '}
                            {dayjs(bilancia.data?.data_ultima_modifica).format('DD.MM.YYYY')})
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    {(isAdmin || isSupervisore) && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="w-100"
                        onClick={async () => {
                          await fetchApi(
                            `${API_URL}/bilance/bilancia/${bilancia.data?.id}/genera-doc-dichiarazione-ue-om/`,
                            {
                              method: 'POST',
                              headers: {
                                Authorization: `Bearer ${tokens?.access}`,
                              },
                            }
                          ).then(() => {
                            bilancia.refetch()
                            toast.success('Documento Dichiarazione UE-om generato con successo', {
                              position: 'bottom-right',
                              autoClose: 5000,
                              hideProgressBar: false,
                              closeOnClick: true,
                              pauseOnHover: true,
                              draggable: true,
                              progress: undefined,
                            })
                          })
                        }}
                      >
                        {bilancia.data?.doc_dichiaraz_ue_om_data_generazione ? 'Rigenera' : 'Genera'}
                      </Button>
                    )}
                  </div>
                  <div className="col-md-2">
                    {(isAdmin || isSupervisore) && (
                      <Button
                        size="sm"
                        variant="secondary"
                        className="w-100 text-white"
                        onClick={() => {
                          inputDocDichiarazUeOm.current?.click()
                        }}
                      >
                        Carica
                      </Button>
                    )}
                    <input
                      type="file"
                      ref={inputDocDichiarazUeOm}
                      hidden
                      onChange={async (e) => {
                        await updatePartialBilancia({
                          id: bilancia.data?.id,
                          doc_dichiaraz_ue_om: e.target.files?.[0],
                          doc_dichiaraz_ue_om_data_generazione: dayjs().format('YYYY-MM-DD'),
                        })
                      }}
                    />
                  </div>
                  <div className="col-md-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="w-100 text-white"
                      onClick={() => {
                        const url = new URL(
                          bilancia.data?.doc_dichiaraz_ue_om
                            ? transformDownloadUrl(String(bilancia.data?.doc_dichiaraz_ue_om))
                            : '#'
                        )
                        window.open(url.toString(), '_blank')
                      }}
                      disabled={!bilancia.data?.doc_dichiaraz_ue_om}
                    >
                      Scarica
                    </Button>
                  </div>
                  {bilancia.data?.doc_dichiaraz_ue_om && !bilancia.data.doc_dichiaraz_ue_om_generato && (
                    <div className="offset-md-6 col-md-6">
                      <div className="p-sm text-body-secondary">Caricato manualmente</div>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          )}
        </div>
        <div className="col-md-6">
          {bilancia.data?.verif_periodica && (
            <CardVerificazionePeriodicaBilancia
              bilancia={bilancia}
              modalActionsAddInterventoVeloce={modalActionsAddInterventoVeloce}
              modalActionsProgrammaIntervento={modalActionsProgrammaIntervento}
              modalActionsAddRichiesta={modalActionsAddRichiesta}
            />
          )}
          <CardRapportoTaraturaBilancia
            bilancia={bilancia}
            rapportoTaratura={bilancia.data?.rapporto_taratura ?? false}
            modalActionsProgrammaIntervento={modalActionsProgrammaIntervento}
            modalActionsAddInterventoVeloce={modalActionsAddInterventoVeloce}
          />
          {bilancia.data?.autoprodotta && <CardCollaudo bilancia={bilancia} />}
          <CardAllegatiBilancia bilancia={bilancia} />
          <CardStoricoInterventiBilancia bilancia={bilancia} />
        </div>
        {modalStateForm.isOpen && (
          <ModalForm<Bilancia>
            title={'Modifica bilancia'}
            isOpen={modalStateForm.isOpen}
            toggle={modalActionsForm.toggle}
            initialValues={modalStateForm.value ?? initialValues}
            save={saveBilancia}
            schema={schema}
          >
            <FormBilancia />
          </ModalForm>
        )}
        {modalStateAddRichiesta.isOpen && (
          <ModalForm<Intervento>
            title="Aggiungi richiesta"
            isOpen={modalStateAddRichiesta.isOpen}
            toggle={modalActionsAddRichiesta.toggle}
            initialValues={modalStateAddRichiesta.value ?? initialValuesIntervento}
            save={saveRichiestaIntervento}
            schema={schemaIntervento}
            size="lg"
          >
            <FormRichiesta bilancia={bilancia.data} />
          </ModalForm>
        )}
        {modalStateAddInterventoVeloce.isOpen && (
          <ModalForm<Intervento>
            title={
              modalStateAddInterventoVeloce.value?.tipo_intervento === 'verifica_periodica'
                ? 'Intervento veloce: verifica periodica'
                : 'Intervento veloce: taratura'
            }
            isOpen={modalStateAddInterventoVeloce.isOpen}
            toggle={modalActionsAddInterventoVeloce.toggle}
            initialValues={modalStateAddInterventoVeloce.value ?? initialValuesIntervento}
            save={saveInterventoVeloce}
            schema={
              modalStateAddInterventoVeloce.value?.tipo_intervento === 'verifica_periodica'
                ? schemaInterventoVelocePeriodica
                : schemaInterventoVeloceTaratura
            }
            size="lg"
          >
            <FormInterventoVeloce bilancia={bilancia.data} />
          </ModalForm>
        )}
        {modalStateProgrammaIntervento.isOpen && (
          <ModalForm<Intervento>
            title={
              modalStateProgrammaIntervento.value?.tipo_intervento === 'verifica_periodica'
                ? 'Programma intervento: verifica periodica'
                : 'Programma intervento: taratura'
            }
            isOpen={modalStateProgrammaIntervento.isOpen}
            toggle={modalActionsProgrammaIntervento.toggle}
            initialValues={modalStateProgrammaIntervento.value ?? initialValuesIntervento}
            save={saveProgrammaIntervento}
            schema={schemaInterventoVeloceTaratura}
            size="lg"
          >
            <FormProgrammaIntervento bilancia={bilancia.data} />
          </ModalForm>
        )}
        {modalStateDelete.isOpen && (
          <ModalConfirm
            isOpen={modalStateDelete.isOpen}
            toggle={modalActionsDelete.toggle}
            title="Elimina bilancia"
            confirmText="Elimina"
            body={'Sei sicuro di voler eliminare la bilancia ' + bilancia.data?.matricola + '?'}
            action={async () => {
              await updatePartialBilancia({
                id: bilancia.data?.id,
                cancellato: true,
              }).then(() => {
                modalActionsDelete.close()
                toast.success('Bilancia eliminata con successo', {
                  position: 'bottom-right',
                  autoClose: 5000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true,
                  progress: undefined,
                })
                navigate('/bilance')
              })
            }}
          />
        )}
      </div>
    </div>
  )
}
